import {isDefined, NaN, noop} from '@acng/frontend-bounty/std/value.js';
import {GlobalActor, globalValue, GlobalWatch} from '@acng/frontend-relativity';
import {iso, now} from '@acng/frontend-bounty/std/date.js';
import {ARRAY, CONST, ENUM, guard, INTEGER, OBJECT, STRING} from '@acng/frontend-rubicon';

import {ctxEventBusUser, getEventBusUser, listen} from '../../userPool/context/event-bus-user.js';
import {get} from '../../core/service/backend.js';
import {ctxVotes} from '../context/list.js';

const VERBOSE = true;

/**
 * @type {['positive', 'negative', 'match']}
 */
const STATES = ['positive', 'negative', 'match'];

const VOTE = /* @__PURE__ */ OBJECT({
  id: INTEGER,
  created_at: STRING,
  state: /* @__PURE__ */ ENUM(STATES),
  amateur_id: INTEGER,
});

const VOTES = /* @__PURE__ */ ARRAY(VOTE);

/**
 * @type {['hot', 'not']}
 */
const OPTIONS = ['hot', 'not'];

const EVENTBUS_VOTED = /* @__PURE__ */ OBJECT({
  type: /* @__PURE__ */ CONST('hon.voted'),
  amateur_id: STRING,
  image_id: INTEGER,
  voting: /* @__PURE__ */ ENUM(OPTIONS),
});

/**
 * @typedef {Valid<VOTE>} Vote
 */

/**
 * @type {Promise<Vote[]> | undefined}
 */
let init;

/**
 * Continously provide the vote list.
 *
 * @returns {Promise<Vote[]>} A promise when called first, each further call
 *   immediately returns `undefined`.
 */
const setup = () => {
  if (init) {
    return init;
  }

  DEBUG: if (VERBOSE) console.warn('Setup vote list.');
  const provideVotes = GlobalActor(ctxVotes);

  /**
   * @param {number | undefined} userId
   *
   * @returns {Promise<Vote[]>}
   */
  const load = async (userId) => {
    if (!userId) {
      provideVotes([]);

      return [];
    }

    const votes = await get('hotornot/votes');
    ASSERT: guard(votes, VOTES);

    const currentUser = getEventBusUser();

    if (userId == currentUser?.id) {
      provideVotes(votes);

      return votes;
    } else {
      provideVotes([]);

      return [];
    }
  };

  GlobalWatch(ctxEventBusUser)((authUser, previous) => {
    if (isDefined(previous)) {
      init = load(authUser?.id);
    }
  });

  listen('hon.voted', async (data) => {
    ASSERT: guard(data, EVENTBUS_VOTED);

    const votes = globalValue(ctxVotes);
    const amateurId = parseInt(data.amateur_id);
    const voteExists = votes.some((vote) => vote.amateur_id == amateurId);

    if (!voteExists) {
      votes.unshift({
        id: NaN,
        created_at: iso(now()),
        state: data.voting == 'hot' ? 'positive' : 'negative',
        amateur_id: amateurId,
      });

      provideVotes(votes);
    }
  });

  init = load(getEventBusUser()?.id);

  return init;
};

export {setup};
